/**
 * Контроллер viewer-aware чата.
 *
 * Держит три инварианта транспорта:
 * - не более одной history operation в полёте на generation;
 * - поздние ответы retired generation отбрасываются (generation guard);
 * - live и delete кадры буферизуются, пока идёт latest-загрузка, и
 *   проигрываются в порядке получения уже после сборки базы.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { com } from "../../../proto/game.pb";
import { useWebSocket } from "../../../shared/contexts";
import { MT } from "../../../shared/types/proto/mt";
import {
  AckInput,
  applyAck,
  createAttempt,
  markTimedOut,
  removeAttempt,
} from "./attempts";
import {
  EMPTY_PROJECTION,
  ProjectionState,
  appendRecord,
  applyTombstone,
  buildBase,
  prependBlock,
  toDisplayList,
} from "./projection";
import { newClientMessageId, validateText } from "./text";
import {
  ChatController,
  ChatMode,
  ChatRecord,
  HistoryPhase,
  SendAttempt,
} from "./types";

const SEND_TIMEOUT_MS = 10_000;

/** События, отложенные на время latest-загрузки. */
type BufferedEvent =
  | { kind: "record"; record: ChatRecord }
  | { kind: "delete"; senderMessageId: string; deletedAt: number };

interface OutstandingOp {
  /** `latest` буферизует live-кадры, `older` применяет их сразу. */
  kind: "latest" | "older";
  generation: number;
  buffer: BufferedEvent[];
}

/** Кадр без `senderMessageId` не меняет state: identity взять не из чего. */
function normalizeRecord(
  payload: com.achlid.crash.game.stateless.IChatMessage | null | undefined
): ChatRecord | null {
  if (!payload?.senderMessageId) return null;
  return {
    senderMessageId: payload.senderMessageId,
    sentAt: Number(payload.sentAt ?? 0),
    playerName: payload.playerName ?? "",
    avatar: payload.avatar ?? "",
    text: payload.text ?? "",
    deleted: Boolean(payload.deleted),
    deletedAt: Number(payload.deletedAt ?? 0),
  };
}

export function useChatController(): ChatController {
  const { generation, chatMode, roomId, status, onMessage, sendProto } =
    useWebSocket();

  const [projection, setProjection] =
    useState<ProjectionState>(EMPTY_PROJECTION);
  const [attempts, setAttempts] = useState<SendAttempt[]>([]);
  const [historyPhase, setHistoryPhase] = useState<HistoryPhase>("idle");
  const [hasMore, setHasMore] = useState(false);

  const generationRef = useRef(generation);
  const outstandingRef = useRef<OutstandingOp | null>(null);
  /** `next_cursor_v2` последнего успешного ответа. Opaque, не разбирается. */
  const cursorRef = useRef("");
  const initialSentForRef = useRef(-1);
  const sendTimersRef = useRef<Map<string, number>>(new Map());

  const connected =
    status === "open" && chatMode === ChatMode.CHAT_MODE_VIEWER_AWARE;

  const clearSendTimers = useCallback(() => {
    sendTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    sendTimersRef.current.clear();
  }, []);

  // Полный сброс на каждую новую попытку соединения или смену комнаты.
  useEffect(() => {
    generationRef.current = generation;
    outstandingRef.current = null;
    cursorRef.current = "";
    initialSentForRef.current = -1;
    clearSendTimers();

    setProjection(EMPTY_PROJECTION);
    setAttempts([]);
    setHistoryPhase("idle");
    setHasMore(false);
  }, [generation, roomId, clearSendTimers]);

  useEffect(() => clearSendTimers, [clearSendTimers]);

  const requestHistory = useCallback(
    (kind: OutstandingOp["kind"], cursor: string) => {
      outstandingRef.current = {
        kind,
        generation: generationRef.current,
        buffer: [],
      };

      const sent = sendProto({
        type: MT.GET_VIEWER_AWARE_CHAT_HISTORY,
        timestamp: Date.now(),
        // limit=0 — серверный default.
        getViewerAwareChatHistory: { limit: 0, cursorV2: cursor },
      });

      if (!sent) {
        outstandingRef.current = null;
        setHistoryPhase("failed");
      }
    },
    [sendProto]
  );

  // Ровно один запрос последней страницы на каждую новую generation.
  useEffect(() => {
    if (!connected || initialSentForRef.current === generation) return;
    initialSentForRef.current = generation;
    setHistoryPhase("loading-initial");
    requestHistory("latest", "");
  }, [connected, generation, requestHistory]);

  // Все чат-кадры одной подписки: guard и буферизация здесь общие.
  useEffect(() => {
    const capturedGeneration = generation;
    const isStale = () => capturedGeneration !== generationRef.current;

    const applyOrBuffer = (event: BufferedEvent) => {
      const outstanding = outstandingRef.current;
      if (outstanding?.kind === "latest") {
        outstanding.buffer.push(event);
        return;
      }
      setProjection((prev) =>
        event.kind === "record"
          ? appendRecord(prev, event.record)
          : applyTombstone(prev, event.senderMessageId, event.deletedAt)
      );
    };

    const offLive = onMessage(MT.CHAT_MESSAGE, (message) => {
      if (isStale()) return;
      const record = normalizeRecord(message.chatMessage);
      if (record) applyOrBuffer({ kind: "record", record });
    });

    // Delete сопоставляется как `event.message_id` -> `record.senderMessageId`.
    const offDelete = onMessage(MT.CHAT_MESSAGE_DELETED, (message) => {
      if (isStale()) return;
      const senderMessageId = message.chatMessageDeleted?.messageId ?? "";
      const deletedAt = Number(message.chatMessageDeleted?.deletedAt ?? 0);
      if (senderMessageId && deletedAt > 0) {
        applyOrBuffer({ kind: "delete", senderMessageId, deletedAt });
      }
    });

    const offAck = onMessage(MT.CHAT_SEND_RESULT, (message) => {
      if (isStale() || !message.chatSendResult) return;
      const result = message.chatSendResult;
      const ack: AckInput = {
        success: Boolean(result.success),
        clientMessageId: result.clientMessageId ?? "",
        errorCode: result.errorCode ?? "",
        senderMessageId: result.senderMessageId ?? "",
      };

      const timer = sendTimersRef.current.get(ack.clientMessageId);
      if (timer != null) {
        window.clearTimeout(timer);
        sendTimersRef.current.delete(ack.clientMessageId);
      }
      setAttempts((prev) => applyAck(prev, ack));
    });

    const offHistory = onMessage(
      MT.VIEWER_AWARE_CHAT_HISTORY_RESPONSE,
      (message) => {
        const outstanding = outstandingRef.current;
        // Ответ без своей operation или от retired generation игнорируется.
        if (
          isStale() ||
          !outstanding ||
          outstanding.generation !== generationRef.current
        ) {
          return;
        }

        const response = message.viewerAwareChatHistoryResponse;
        if (!response) return;
        outstandingRef.current = null;

        const replayBuffer = (state: ProjectionState) =>
          outstanding.buffer.reduce(
            (acc, event) =>
              event.kind === "record"
                ? appendRecord(acc, event.record)
                : applyTombstone(acc, event.senderMessageId, event.deletedAt),
            state
          );

        if (!response.success) {
          // Неудача — не пустая страница: рендер, курсор и hasMore сохраняются.
          setProjection(replayBuffer);
          if (response.errorCode === "INVALID_CURSOR") {
            cursorRef.current = "";
            setHistoryPhase("reloading");
            requestHistory("latest", "");
          } else {
            setHistoryPhase("failed");
          }
          return;
        }

        const records = (response.messages ?? [])
          .map(normalizeRecord)
          .filter((record): record is ChatRecord => record !== null);

        setProjection((prev) =>
          outstanding.kind === "older"
            ? prependBlock(prev, records)
            : // Свежая база из ответа, затем отложенные кадры — одной заменой.
              replayBuffer(buildBase(records))
        );

        cursorRef.current = response.nextCursorV2 ?? "";
        setHasMore(Boolean(response.hasMore));
        setHistoryPhase("idle");
      }
    );

    return () => {
      offLive();
      offDelete();
      offAck();
      offHistory();
    };
  }, [onMessage, generation, requestHistory]);

  const send = useCallback(
    (raw: string) => {
      if (!connected) return;

      const { canonical, canSend } = validateText(raw);
      if (!canSend) return;

      const clientMessageId = newClientMessageId();
      setAttempts((prev) => createAttempt(prev, clientMessageId, canonical));

      const sent = sendProto({
        type: MT.SEND_CHAT_MESSAGE,
        timestamp: Date.now(),
        sendChatMessage: { text: canonical, clientMessageId },
      });

      if (!sent) {
        setAttempts((prev) => removeAttempt(prev, clientMessageId));
        return;
      }

      const capturedGeneration = generationRef.current;
      const timer = window.setTimeout(() => {
        sendTimersRef.current.delete(clientMessageId);
        if (capturedGeneration !== generationRef.current) return;
        setAttempts((prev) => markTimedOut(prev, clientMessageId));
      }, SEND_TIMEOUT_MS);
      sendTimersRef.current.set(clientMessageId, timer);
    },
    [connected, sendProto]
  );

  const loadOlder = useCallback(() => {
    if (!connected || !hasMore || historyPhase !== "idle") return;
    if (outstandingRef.current || !cursorRef.current) return;
    setHistoryPhase("loading-older");
    requestHistory("older", cursorRef.current);
  }, [connected, hasMore, historyPhase, requestHistory]);

  const reloadLatest = useCallback(() => {
    if (!connected || outstandingRef.current) return;
    setHistoryPhase("reloading");
    requestHistory("latest", "");
  }, [connected, requestHistory]);

  const retryAttempt = useCallback(
    (clientMessageId: string) => {
      const target = attempts.find(
        (attempt) => attempt.clientMessageId === clientMessageId
      );
      if (!target) return;
      setAttempts((prev) => removeAttempt(prev, clientMessageId));
      send(target.text);
    },
    [attempts, send]
  );

  const records = useMemo(() => toDisplayList(projection), [projection]);

  const ownSenderMessageIds = useMemo(() => {
    const own = new Set<string>();
    for (const attempt of attempts) {
      if (attempt.senderMessageId) own.add(attempt.senderMessageId);
    }
    return own;
  }, [attempts]);

  /**
   * Подтверждённые attempts скрываются: их запись уже пришла own live-кадром
   * либо придёт через мгновение, и дублировать её строкой не нужно.
   */
  const pendingAttempts = useMemo(
    () =>
      attempts.filter(
        (attempt) =>
          attempt.state !== "acknowledged" ||
          !projection.records[attempt.senderMessageId ?? ""]
      ),
    [attempts, projection]
  );

  return {
    mode: chatMode,
    connected,
    records,
    pendingAttempts,
    ownSenderMessageIds,
    historyPhase,
    hasMore,
    send,
    loadOlder,
    reloadLatest,
    retryAttempt,
  };
}
